import { useState } from 'react';
import type { ChatMessage, EntityDetail } from '../api/client.js';

export interface AiProposal {
  delta: Record<string, unknown>;
  message: string;
  document: EntityDetail;
  provider?: 'bedrock' | 'local_fallback';
  status: 'pending' | 'applied' | 'rejected';
}

export function AiChatPane({
  workingCopy,
  chatHistory,
  pendingProposal,
  canUndo,
  isSending,
  onSendMessage,
  onApplyProposal,
  onRejectProposal,
  onUndo,
  className = '',
  embedded = false,
}: {
  workingCopy: EntityDetail | null;
  chatHistory: ChatMessage[];
  pendingProposal: AiProposal | null;
  canUndo: boolean;
  isSending: boolean;
  onSendMessage: (instruction: string) => void;
  onApplyProposal: () => void;
  onRejectProposal: () => void;
  onUndo: () => void;
  className?: string;
  embedded?: boolean;
}) {
  const [instruction, setInstruction] = useState('');

  function handleSubmit() {
    const trimmed = instruction.trim();
    if (!trimmed || !workingCopy || isSending) {
      return;
    }
    onSendMessage(trimmed);
    setInstruction('');
  }

  const panelClass = embedded
    ? `grid min-w-0 grid-rows-[auto_1fr_auto] h-full overflow-hidden ${className}`.trim()
    : `editor-panel grid min-w-0 grid-rows-[auto_1fr_auto] overflow-hidden xl:h-full xl:min-h-0 ${className}`.trim();

  return (
    <aside className={panelClass}>
      <div className="flex items-center gap-3 border-b border-primary/8 px-5 py-4">
        <div className="grid size-9 place-items-center rounded-xl bg-primary/8 text-sm font-bold text-primary">AI</div>
        <div>
          <div className="font-semibold text-slate-900">AI Assistant</div>
          <div className="text-xs text-slate-500">Claude Sonnet 4.6</div>
        </div>
      </div>

      <div className="grid content-start gap-3 overflow-y-auto p-4">
        {chatHistory.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            className={
              message.role === 'user'
                ? 'grid justify-self-end gap-1 rounded-2xl rounded-tr-md bg-primary px-4 py-3 text-sm leading-6 text-cream shadow-sm'
                : 'grid gap-1 rounded-2xl rounded-tl-md border border-primary/10 bg-primary/5 px-4 py-3 text-sm leading-6 text-slate-700'
            }
          >
            <div className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] opacity-70">
              {message.role === 'user' ? 'You' : 'Assistant'}
            </div>
            <div>{message.content}</div>
          </div>
        ))}

        {pendingProposal ? (
          <div className="grid gap-3 rounded-3xl border border-primary/12 bg-slate-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="font-semibold text-slate-900">Proposed change</div>
              <div className="flex items-center gap-2">
                {pendingProposal.provider ? (
                  <span className="editor-badge">
                    {pendingProposal.provider === 'bedrock' ? 'Bedrock' : 'Local fallback'}
                  </span>
                ) : null}
                <span className="editor-badge">Review before apply</span>
              </div>
            </div>
            <div className="text-sm leading-6 text-slate-700">{pendingProposal.message}</div>
            <pre className="overflow-auto rounded-2xl bg-white p-3 text-xs leading-5 text-slate-700 shadow-inner">
              {JSON.stringify(pendingProposal.delta, null, 2)}
            </pre>
            <div className="flex flex-wrap items-center gap-3">
              {pendingProposal.status === 'pending' ? (
                <>
                  <button
                    className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-cream transition hover:bg-primary/90"
                    onClick={onApplyProposal}
                    type="button"
                  >
                    Apply
                  </button>
                  <button
                    className="rounded-full border border-primary/16 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-primary/5"
                    onClick={onRejectProposal}
                    type="button"
                  >
                    Reject
                  </button>
                </>
              ) : pendingProposal.status === 'applied' ? (
                <>
                  <span className="font-semibold text-emerald-700">Applied ✓</span>
                  {canUndo ? (
                    <button className="text-sm font-medium text-sky-700 transition hover:text-sky-800" onClick={onUndo} type="button">
                      Undo
                    </button>
                  ) : (
                    <span className="text-xs text-slate-500">Undo unavailable after subsequent changes</span>
                  )}
                </>
              ) : (
                <span className="text-xs font-medium text-slate-500">Rejected</span>
              )}
            </div>
          </div>
        ) : null}

        {isSending ? <div className="text-sm text-slate-500">Generating proposal…</div> : null}
      </div>

      <div className="border-t border-primary/8 bg-white/95 p-4">
        <div className="relative">
          <textarea
            className="min-h-[88px] w-full resize-none rounded-2xl border border-primary/12 bg-white px-4 py-3 pr-12 text-sm leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-primary/30 focus:ring-0"
            onChange={(event) => setInstruction(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                handleSubmit();
              }
            }}
            placeholder="Ask AI to edit this entity…"
            value={instruction}
          />
          <button
            className="absolute bottom-3 right-3 grid size-8 place-items-center rounded-xl bg-primary text-cream transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
            disabled={!instruction.trim() || isSending}
            onClick={handleSubmit}
            type="button"
            aria-label="Send"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M7 12V2M3 6l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}
